import toNumber from 'lodash/toNumber'
import type { PivotMember } from './types'
import type { PivotField } from '@ztechapps/common.contract'
import { DirectionEnum, PivotDateGroupEnum, PivotFieldTypeEnum } from '@ztechapps/common.contract'
import { formatConstants, toDate } from '@ztechapps/common.utils'
import { PIVOT_BLANK_LABEL } from './constants'

// A full ISO instant reads the same through `Date` and moment; anything else (a date-only string)
// parses as local time in moment, so it keeps the moment path.
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T[\d:.]+(Z|[+-]\d{2}:?\d{2})$/
const LOCAL_DAY = /^\d{4}-\d{2}-\d{2}$/
const dayFormatters = new Map<string, Intl.DateTimeFormat>()
// a row's filter check and its tree path read the same value back to back — the last day is kept
let lastDay: { value: unknown; timeZone: string; day: string } | undefined

/** The `YYYY-MM-DD` day of a date value in a time zone — a cached formatter for an ISO instant, moment otherwise. */
const toLocalDay = (value: unknown, timeZone: string): string => {
  if (lastDay?.value === value && lastDay.timeZone === timeZone) return lastDay.day
  const day = readLocalDay(value, timeZone)
  lastDay = { value, timeZone, day }
  return day
}

const readLocalDay = (value: unknown, timeZone: string): string => {
  if (typeof value === 'number' || (typeof value === 'string' && ISO_INSTANT.test(value))) {
    let formatter = dayFormatters.get(timeZone)
    if (!formatter) {
      formatter = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
      dayFormatters.set(timeZone, formatter)
    }
    const day = formatter.format(new Date(typeof value === 'number' ? value * 1000 : value))
    if (LOCAL_DAY.test(day)) return day
  }
  return toDate(value as string, timeZone).format('YYYY-MM-DD')
}

/** Groups a raw field value into its member key without building the label. */
export const toPivotMemberKey = (
  value: unknown,
  field: PivotField,
  dateGroup: PivotDateGroupEnum | undefined,
  timeZone: string
): string => {
  if (value === null || value === undefined || value === '') return ''
  if (field.type !== PivotFieldTypeEnum.Date) return String(value)
  const day = toLocalDay(value, timeZone)
  if (dateGroup === PivotDateGroupEnum.Year) return day.slice(0, 4)
  if (dateGroup === PivotDateGroupEnum.Quarter) return `Q${Math.ceil(toNumber(day.slice(5, 7)) / 3)}`
  if (dateGroup === PivotDateGroupEnum.Month) return day.slice(5, 7)
  return day
}

/** Groups a raw field value into a pivot member. */
export const toPivotMember = (
  value: unknown,
  field: PivotField,
  dateGroup: PivotDateGroupEnum | undefined,
  timeZone: string
): PivotMember => {
  if (value === null || value === undefined || value === '') {
    return { key: '', label: PIVOT_BLANK_LABEL, sortValue: '', isBlank: true }
  }
  const key = toPivotMemberKey(value, field, dateGroup, timeZone)
  if (field.type === PivotFieldTypeEnum.Date) {
    // only the Month and Day labels need moment — Year and Quarter read as their key
    if (dateGroup === PivotDateGroupEnum.Month)
      return { key, label: toDate(value as string, timeZone).format('MMM'), sortValue: key, isBlank: false }
    if (dateGroup === undefined)
      return {
        key,
        label: toDate(value as string, timeZone).format(formatConstants.date),
        sortValue: key,
        isBlank: false,
      }
    return { key, label: key, sortValue: key, isBlank: false }
  }
  if (field.type === PivotFieldTypeEnum.Number) {
    return { key, label: key, sortValue: toNumber(value), isBlank: false }
  }
  return { key, label: key, sortValue: key, isBlank: false }
}

const compareSortValue = (left: string | number, right: string | number): number => {
  if (typeof left === 'number' && typeof right === 'number') {
    if (left < right) return -1
    if (left > right) return 1
    return 0
  }
  const leftKey = String(left)
  const rightKey = String(right)
  if (leftKey < rightKey) return -1
  if (leftKey > rightKey) return 1
  return 0
}

/** Sorts pivot-wrapped members, blanks last in both directions. */
export const sortPivotMembers = <T extends { member: PivotMember }>(
  items: T[],
  direction: DirectionEnum = DirectionEnum.asc
): T[] => {
  return [...items].sort((a, b) => {
    if (a.member.isBlank !== b.member.isBlank) return a.member.isBlank ? 1 : -1
    const order = compareSortValue(a.member.sortValue, b.member.sortValue)
    return direction === DirectionEnum.desc ? -order : order
  })
}
